<template>
  <b-overlay class="animated fadeIn">
    <template v-if="filtered_samples">
      <b-row class="my-3 pr-3">
        <b-col class="pr-0">
          <toggle-batch-button
            :max="samples_on_page"
            :submitting="toggles.submitting_select"
            :side="toggles.select_from"
            :value="toggles.select_value"
            :options="toggles.select_options"
            :disable="disableToggleButton('select')"
            process="select"
            @applyToggle="applyToggle"
            @updateToggleSelector="updateToggleSelector"
            @updateToggleValue="updateToggleValue"
          />
        </b-col>
        <b-col class="pr-0">
          <toggle-batch-button
            :max="samples_on_page"
            :submitting="toggles.submitting_remove"
            :side="toggles.remove_from"
            :value="toggles.remove_value"
            :options="toggles.remove_options"
            :disable="disableToggleButton('remove')"
            process="remove"
            @applyToggle="applyToggle"
            @updateToggleSelector="updateToggleSelector"
            @updateToggleValue="updateToggleValue"
          />
        </b-col>
        <b-col cols="2" class="pr-0">
          <b-button
            block
            @click="handleToggleAll('check')"
            :disabled="!filtered_samples || !filtered_samples.length"
          >
            <template v-if="toggles.submitting_check_all">
              <i class="fal fa-spinner fa-spin" /> {{ " Update..." }}
            </template>
            <template v-else>
              <i class="fal fa-check-square" /> Select All
            </template>
          </b-button>
        </b-col>
        <b-col cols="2" class="pr-0">
          <b-button
            block
            @click="handleToggleAll('uncheck')"
            :disabled="!filtered_samples || !filtered_samples.length"
          >
            <template v-if="toggles.submitting_uncheck_all">
              <i class="fal fa-spinner fa-spin" />{{ " Update..." }}
            </template>
            <template v-else> <i class="fal fa-square" /> Remove All </template>
          </b-button>
        </b-col>
      </b-row>
      <template v-if="filtered_samples.length">
        <div class="instrument_sample_table">
          <b-table
            :items="filtered_samples"
            :fields="table.fields"
            :per-page="table.per_page"
            v-sortable="table.sorts"
            :current-page="table.current_page"
            id="schedule-samples"
            responsive
          >
            <template #cell(schedule)="data">
              <schedule-sample-checkbox
                :sample="data.item"
                :instrument_instance_schedules="
                  schedule_process.instrument_instance_schedules
                "
                :sample_payload="sample_payload"
                :schedule_process_id="schedule_process.id"
                placement="process_instrument_table"
                @updateInstrumentSampleList="updateInstrumentSampleList"
              />
            </template>
            <template #cell(order)="data">
              {{ getOrder(data.item) }}
            </template>
            <template #cell(jig)="data">
              {{ getJig(data.item) }}
            </template>
            <template #cell(pid)="data">
              <router-link :to="`/samples/${data.item.id}`">
                <b>{{ data.item.publicNumber() }}</b>
              </router-link>
            </template>
            <template #cell(receiving_id)="data">
              {{ data.item.receiving_id }}
            </template>
            <template #cell(customer)="data">
              <router-link
                :to="`/customer/'${getCustomerData(data.item, 'id')}`"
              >
                {{ getCustomerData(data.item, "name") }}
              </router-link>
            </template>
            <template #cell(submission_pid)="data">
              <router-link :to="`/samples/${data.item.id}`">
                {{ getSubmissionPid(data.item) }}
              </router-link>
            </template>
            <template #cell(identifier)="data">
              {{ data.item.identifier }}
            </template>
            <template #cell(number)="data">
              {{ data.item.number }}
            </template>
            <template #cell(created_at)="data">
              {{ data.item.created_at | moment("L") }}
            </template>
            <template #cell(turnaround)="data">
              {{
                data.item.submission.turnaround
                  ? data.item.submission.turnaround.name
                  : null
              }}
            </template>
          </b-table>
        </div>
        <custom-table-pagination
          :options="table.per_page_options"
          :current_page="table.current_page"
          :per_page="table.per_page"
          :total_rows="filtered_samples.length"
          @update="updatePaginationData"
          table_id="schedule-samples"
          selector_prefix="Samples per page"
        />
      </template>
      <div v-else>
        <b-alert show class="mb-4">No samples were found.</b-alert>
      </div>
    </template>
  </b-overlay>
</template>
<script>
import ScheduleSampleCheckbox from "./form/components/ScheduleSampleCheckbox";
import CustomTablePagination from "@/components/shared/CustomTablePagination";
import sortable from "@/components/shared/directives/Sortable";
import ToggleBatchButton from "@/components/shared/form/ToggleBatchButton";
import filterSamples from "@/shared/filter-samples";
import { mapActions, mapGetters } from "vuex";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";

const ProcessScheduleRepository = LabRepositoryFactory.get("processSchedule");

export default {
  name: "ScheduledSamplesServerTable",
  components: {
    ToggleBatchButton,
    CustomTablePagination,
    ScheduleSampleCheckbox,
  },
  directives: { sortable },
  props: {
    filter: {
      type: Object,
      default: () => null,
    },
    sample_collection: {
      type: Object,
      default: () => null,
    },
    schedule_process: {
      type: Object,
      default: () => null,
    },
    schedule_task: {
      type: Object,
      default: () => null,
    },
    instrument_jig_collection: {
      type: Object,
      default: () => null,
    },
    picked_instrument: {
      type: String,
      default: null,
    },
    picked_instrument_jig: {
      type: Object,
      default: () => null,
    },
  },
  computed: {
    ...mapGetters({
      schedule_sample_list: "scheduleSampleList",
    }),
    filtered_samples() {
      return this.sample_collection
        ? filterSamples(
            this.sample_collection.models,
            this.filter,
            this.schedule_sample_list,
            this.sample_payload
          )
        : null;
    },
    instrument_instance_id() {
      return this.picked_instrument;
    },
    samples_on_page() {
      if (!this.filtered_samples) {
        return null;
      }

      if (!this.filtered_samples.length) {
        return 0;
      }

      if (
        this.table.per_page * this.table.current_page <=
        this.filtered_samples.length
      ) {
        return this.table.per_page;
      } else {
        return (
          this.filtered_samples.length -
          this.table.per_page * (this.table.current_page - 1)
        );
      }
    },
    sample_payload() {
      return {
        instrument_instance_id: this.instrument_instance_id,
        instrument_jig_id:
          this.picked_instrument_jig &&
          this.picked_instrument_jig.instrument_jig_id,
        schedulable_task_type: `${this.schedule_process.schedulable_process_type.replace("Process", "Task")}`,
        schedulable_task_id: this.schedule_task.id,
      };
    },
  },
  data() {
    return {
      toggles: {
        select_options: [
          { text: "Select From Top", value: "top" },
          { text: "Select From Bottom", value: "bottom" },
        ],
        remove_options: [
          { text: "Remove From Top", value: "top" },
          { text: "Remove From Bottom", value: "bottom" },
        ],
        select_from: "top",
        remove_from: "top",
        select_value: 0,
        remove_value: 0,
        submitting_select: false,
        submitting_remove: false,
        submitting_check_all: false,
        submitting_uncheck_all: false,
      },
      table: {
        fields: [
          { key: "schedule", label: "Schedule" },
          { key: "order", label: "Order" },
          { key: "jig", label: "Jig" },
          { key: "pid", label: "Sample" },
          { key: "receiving_id", label: "RFID" },
          { key: "customer", label: "Customer" },
          { key: "submission_pid", label: "Submission" },
          { key: "identifier", label: "ID" },
          { key: "number", label: "Num." },
          { key: "created_at", label: "Submitted" },
          { key: "turnaround", label: "Turnaround" },
        ],
        sorts: null,
        per_page: 500,
        current_page: 1,
        per_page_options: [500, 750, 1000, 1250],
      },
    };
  },
  methods: {
    ...mapActions(["UPDATE_SCHEDULE_SAMPLE_LIST"]),
    updatePaginationData({ field, value }) {
      this.table[field] = value;
    },
    updateToggleSelector({ process, value }) {
      this.toggles[`${process}_from`] = value;
    },
    updateToggleValue({ process, value }) {
      this.toggles[`${process}_value`] = value;
    },
    disableToggleAllButton() {
      return !this.filtered_samples || !this.filtered_samples.length;
    },
    disableToggleButton(process) {
      return (
        this.disableToggleAllButton() ||
        !this.toggles[`${process}_value`] ||
        this.toggles[`${process}_value`] > this.samples_on_page
      );
    },
    getSubmissionPid(sample) {
      const submission = sample.getSubmission();

      return submission.pid && submission.publicNumber();
    },
    getCustomerData(sample, data) {
      const submission = sample.getSubmission();

      return submission.id && submission.getProject().getCustomer()[data];
    },
    toggleSampleCheck(sample, toggled_sample) {
      const { instrument_instance_id, schedulable_task_id } =
        this.sample_payload;

      return (
        sample.sample_id === toggled_sample.id &&
        sample.instrument_instance_id === instrument_instance_id &&
        sample.schedulable_task_id === schedulable_task_id
      );
    },
    toggleSingleSample(list, toggled_sample, process) {
      const sampleCheck = !!list.find((sample) =>
        this.toggleSampleCheck(sample, toggled_sample)
      );

      if (process === "remove") {
        return sampleCheck
          ? list.filter(
              (sample) => !this.toggleSampleCheck(sample, toggled_sample)
            )
          : list;
      } else {
        return sampleCheck
          ? list
          : [...list, { ...this.sample_payload, sample_id: toggled_sample.id }];
      }
    },
    updateInstrumentSampleList(update_data) {
      this.UPDATE_SCHEDULE_SAMPLE_LIST(
        update_data.map(
          ({
            sample_id,
            instrument_instance_id,
            instrument_jig_id,
            schedulable_task_type,
            schedulable_task_id,
          }) => {
            return {
              sample_id,
              instrument_instance_id,
              instrument_jig_id,
              schedulable_task_type,
              schedulable_task_id,
            };
          }
        )
      );
      this.$emit("updateSchedule");
    },
    getOrder({ id }) {
      return this.filtered_samples.findIndex((item) => item.id === id) + 1;
    },
    getJig({ id }) {
      const schediled_sample =
        this.schedule_process.instrument_instance_schedules ? this.schedule_process.instrument_instance_schedules.find(
          ({ sample_id }) => sample_id === id
        ) : null;
      const instrument_jig = schediled_sample
        ? this.instrument_jig_collection.models.find(
            ({ id }) => id === schediled_sample.instrument_jig_id
          )
        : null;

      return instrument_jig ? instrument_jig.jig.name : null;
    },
    async syncInstrumentSamples({ update, process, action }) {
      try {
        const { data } = await ProcessScheduleRepository.syncSamples(
          this.schedule_process.id,
          update
        );

        this.updateInstrumentSampleList(data);
        this.$bvToast.toast("Schedule sample list was updated.", {
          title: "Update successful.",
          variant: "success",
        });
      } catch ({ response }) {
        this.$bvToast.toast(response.data.error, {
          title: "Update Failed.",
          variant: "danger",
        });
      }

      process && (this.toggles[`submitting_${process}`] = false);
      action && (this.toggles[`submitting_${action}_all`] = false);
    },
    applyToggle(process, side) {
      this.toggles[`submitting_${process}`] = true;

      let update = this.lodash.cloneDeep(this.schedule_sample_list);
      let counter = 0;
      const page_multiplier = this.table.current_page - 1;

      while (this.toggles[`${process}_value`] > counter) {
        const top_border = this.table.per_page * page_multiplier + counter;
        const bottom_border =
          this.table.per_page * page_multiplier +
          this.samples_on_page -
          1 -
          counter;
        const toggled_sample =
          this.filtered_samples[side === "top" ? top_border : bottom_border];

        update = this.toggleSingleSample(update, toggled_sample, process);
        counter++;
      }

      this.syncInstrumentSamples({ update, process });
    },
    handleToggleAll(action) {
      this.toggles[`submitting_${action}_all`] = true;

      const update = this.filtered_samples.reduce((acc, cur) => {
        if (action === "check") {
          const sampleCheck = acc.find((sample) =>
            this.toggleSampleCheck(sample, cur)
          );

          return sampleCheck
            ? acc
            : [...acc, { ...this.sample_payload, sample_id: cur.id }];
        } else {
          return acc.filter((sample) => !this.toggleSampleCheck(sample, cur));
        }
      }, this.schedule_sample_list);

      this.syncInstrumentSamples({ update, action });
    },
  },
};
</script>
